import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { BackArrowSvg } from '../../../assets/event';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { selectChatUnreadTotal } from '../../../store/chat/chatReducer';
import { notificationsRequest } from '../../../store/userNotifications/notificationsActions';
import { selectUnreadCount } from '../../../store/userNotifications/notificationsReducer';
import { RootStackParamList } from '../../navigator';
import HeaderWallet from '../../shell/header/HeaderWallet';
import MenuButton from '../../shell/header/MenuButton';
import NotificationButton from '../../shell/header/NotificationButton';
import Profile from '../../shell/header/Profile';
import LoginButton, { LOGIN_BUTTON } from '../common/LoginButton';
import ScaledBox from '../common/ScaledBox';
import { TouchableOpacity } from '../common/Touchable';
import { DESKTOP_TOP_BAR, useDesktopLayout } from './desktopLayout';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// Figma "Top Bar" (1625:4508, "mackbook air"): the avatar on the left, the wallet centred, the
// notification and settings buttons on the right - every piece the mobile redesign's top bar
// draws, at 1.62x. The wallet (161x43 -> 260.82x69.66), the buttons (44x43 -> 71.28x69.66) and
// the avatar (54 -> 87.48) all scale by the same 1.62, so they are the mobile components drawn
// bigger rather than a second set.
const MOBILE_TO_DESKTOP = 1.62;
const AVATAR = 54;
const WALLET = { width: 161, height: 43 } as const;
const BUTTON = { width: 44, height: 43 } as const;
/** The LOG IN button hugs its label; 86 is the English one in the file. */
const LOGIN_SLOT = { width: 86, height: LOGIN_BUTTON.height } as const;
/** Figma "Actions": 19.44 between the two buttons. */
const BUTTON_GAP = 19.44;
/** Space between the bar's groups - the avatar or back arrow, and the buttons. */
const PILL_GAP = 24;
/** The mobile back arrow: a 40x40 disc. */
const BACK = 40;

// Mirrors TopSection: the routes the design gives a back arrow instead of the avatar, and the
// titles printed by the arrow. The missions / free-chips pills never sit in this bar - they are
// Home's alone, and Home draws them in its own left column (Figma "Pills").
const BACK_BUTTON_ROUTES = ['Room', 'Tournament', 'ActiveTournament', 'Event'];
const BACK_BUTTON_TITLES: Record<string, string> = { Event: 'EVENT' };

const DesktopTopBar = () => {
	const { navigate, goBack } = useNavigation<Nav>();
	const route = useRoute();
	const isLogged = useSelector(selectIsLogged);
	const unread = useSelector(selectUnreadCount);
	const chatUnread = useSelector(selectChatUnreadTotal);
	const dispatch = useDispatch();
	const { s, scale, side } = useDesktopLayout();
	const k = scale * MOBILE_TO_DESKTOP;
	const showBackButton = BACK_BUTTON_ROUTES.includes(route.name);
	const backTitle = BACK_BUTTON_TITLES[route.name];
	const styles = createStyles(s, side);

	// The mobile header's Nav asks for the unread count on mount; this bar replaces it here.
	useEffect(() => {
		dispatch(notificationsRequest());
	}, [dispatch]);

	return (
		<View pointerEvents='box-none' style={styles.bar}>
			<View pointerEvents='box-none' style={[styles.group, { gap: s(PILL_GAP) }]}>
				{showBackButton ? (
					<View style={[styles.backRow, { gap: s(16) }]}>
						<ScaledBox width={BACK} height={BACK} scale={k}>
							<TouchableOpacity onPress={() => goBack()} style={styles.backButton}>
								<BackArrowSvg width={12} height={20} />
							</TouchableOpacity>
						</ScaledBox>
						{backTitle ? (
							<Text numberOfLines={1} style={styles.backTitle}>
								{backTitle}
							</Text>
						) : null}
					</View>
				) : isLogged ? (
					<ScaledBox width={AVATAR} height={AVATAR} scale={k}>
						<Profile avatarOnly />
					</ScaledBox>
				) : (
					// Figma "Sign Up Button" (5911:55879) is drawn at the mobile bar's size, like the
					// buttons beside it, and lifted to this bar's scale with them.
					<ScaledBox width={LOGIN_SLOT.width} height={LOGIN_SLOT.height} scale={k}>
						<LoginButton />
					</ScaledBox>
				)}
			</View>

			{isLogged ? (
				<View pointerEvents='box-none' style={styles.center}>
					<ScaledBox width={WALLET.width} height={WALLET.height} scale={k}>
						<HeaderWallet />
					</ScaledBox>
				</View>
			) : null}

			<View pointerEvents='box-none' style={[styles.group, { gap: s(PILL_GAP) }]}>
				<View style={[styles.group, { gap: s(BUTTON_GAP) }]}>
					{/* Logged out, the design's bar has no bell - just LOG IN and settings. */}
					{isLogged ? (
						<ScaledBox width={BUTTON.width} height={BUTTON.height} scale={k}>
							<NotificationButton
								unread={unread}
								dot={chatUnread > 0}
								onPress={() => navigate('Messages')}
							/>
						</ScaledBox>
					) : null}
					{/* Figma calls it the "Settings Button": it opens the settings popup. */}
					<ScaledBox width={BUTTON.width} height={BUTTON.height} scale={k}>
						<MenuButton onPress={() => navigate('Settings')} />
					</ScaledBox>
				</View>
			</View>
		</View>
	);
};

const createStyles = (s: (v: number) => number, side: number) =>
	StyleSheet.create({
		bar: {
			position: 'absolute',
			top: s(DESKTOP_TOP_BAR.top),
			left: side,
			right: side,
			height: s(DESKTOP_TOP_BAR.height),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			zIndex: 60,
		},
		group: {
			flexDirection: 'row',
			alignItems: 'center',
		},
		center: {
			...StyleSheet.absoluteFillObject,
			alignItems: 'center',
			justifyContent: 'center',
		},
		backRow: {
			flexDirection: 'row',
			alignItems: 'center',
		},
		backButton: {
			width: BACK,
			height: BACK,
			borderRadius: BACK / 2,
			borderWidth: 1,
			borderColor: '#FFF908',
			backgroundColor: '#4A1004',
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#FFB21A',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.55,
			shadowRadius: 5,
		},
		backTitle: {
			color: '#FFF5EB',
			fontFamily: 'Sofia Sans Semi Condensed',
			fontSize: s(13 * MOBILE_TO_DESKTOP),
			fontWeight: '700',
			letterSpacing: s(1.6 * MOBILE_TO_DESKTOP),
		},
	});

export default DesktopTopBar;
